import { Toolbar, Item, DataGridRef } from "devextreme-react/data-grid";
import { useRef } from "react";
import { useDispatch } from "react-redux";
import { openPopup } from "../../helper/DxHelper";
import { basicSettings } from "../../helper/GridCreator/gridConfig";
import GridCreator from "../../helper/GridCreator/GridCreator";
import type { Dictionary, GridType } from "@/types/types";
import { DictionaryDS } from "../../dataSources/DictionaryDS";
import Update from "./Update";
import { dictionaryActions } from "../../redux/dictionary/dictionary-slice";
import Insert from "./Insert";
import { useDictionaryDelete } from "@/hooks/useDictionaryDelete";
import { confirm } from "devextreme/ui/dialog";
import useHeaderChangeNoUP from "@/hooks/useHeaderChangeNoUP";
import { PopupRef } from "devextreme-react/cjs/popup";
import { ColumnCellTemplateData } from "devextreme/ui/data_grid";
import { useAppSelector } from "@/hooks/useAppSelector";

export default function OpenDictionary() {
  useHeaderChangeNoUP("Open Dictionary");
  const datagridRef = useRef<DataGridRef>(null);
  const updatePopupRef = useRef<PopupRef>(null);
  const insertPopupRef = useRef<PopupRef>(null);
  const dispatch = useDispatch();
  const { user } = useAppSelector((state) => state.system);
  const { mutate: remove } = useDictionaryDelete();

  const addButtonOptions = {
    icon: "plus",
    onClick: () => {
      openPopup(insertPopupRef);
    },
  };

  function renderToolbar() {
    return (
      <Toolbar>
        <Item
          location="after"
          locateInMenu={"auto"}
          widget="dxButton"
          options={addButtonOptions}
          cssClass="dx-datagrid-insert-button"
        />
        <Item name="columnChooserButton" location="after" />
        <Item name="searchPanel" location="after" />
      </Toolbar>
    );
  }

  const gridSettings: GridType<Dictionary, string> = {
    ...basicSettings,
    ref: datagridRef,
    id: "suGrid",
    dataSource: DictionaryDS(),
    toolbar: renderToolbar,
    columns: [
      {
        key: "cmd",
        width: user?.isAdmin ? 90 : 50,
        cellRender: cmdCellRender,
        allowExporting: false,
        allowHiding: false,
        allowResizing: false,
        showInColumnChooser: false,
      },
      {
        key: "id",
        dataField: "id",
        visible: false,
        allowEditing: false,
      },
      {
        key: "code",
        dataType: "string",
        dataField: "code",
        visible: true,
        allowEditing: false,
      },
      {
        key: "name",
        dataType: "string",
        dataField: "name",
        visible: true,
        allowEditing: false,
      },
      {
        key: "type",
        dataType: "string",
        dataField: "type",
        cellRender: (e) => <span>{e.row.data.typeName}</span>,
        visible: true,
        allowEditing: false,
      },
      {
        key: "value",
        dataType: "string",
        dataField: "value",
        visible: true,
        allowEditing: false,
      },
      {
        key: "parent",
        dataType: "string",
        dataField: "parent.name",
        visible: true,
        allowEditing: false,
      },

      {
        key: "comment",
        dataType: "string",
        dataField: "comment",
        visible: true,
        allowEditing: false,
        width: 400,
      },
    ],
  };

  function cmdCellRender(e: ColumnCellTemplateData<Dictionary, string>) {
    return (
      <div className="flex items-center gap-x-2">
        <button
          title="edit"
          onClick={() => {
            openPopup(updatePopupRef);
            dispatch(dictionaryActions.setEditDataId(e.row.data.id));
          }}
        >
          <img src="/icons/edit-icon.svg" />
        </button>
        {e.row.data.canDelete && user?.isAdmin ? (
          <button
            title="delete"
            onClick={() => {
              const { id, name } = e.row.data;
              const res = confirm(`Do you really want to remove ${name}?`, `Remove ${name}`);
              res.then((val) => {
                if (val) remove(id);
              });
            }}
          >
            <img src="/icons/trash-icon.svg" />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {user?.isAdmin ? (
        <>
          <Insert popupRef={insertPopupRef} datagridRef={datagridRef} />
          <Update popupRef={updatePopupRef} datagridRef={datagridRef} />
          <GridCreator {...gridSettings} />
        </>
      ) : null}
    </>
  );
}
